<template>
  <div v-if="modelWorkspace" class="view-workspacemodel">
    <!-- BREADCRUMPS -->
    <div
      v-if="folder"
      class="breadcrump-trail view-workspacemodel__breadcrumps"
    >
      <router-link
        class="breadcrump-trail__item"
        :to="getRouteToFolder(folder)"
      >
        <lucide-icon name="chevron-left" class="breadcrump-trail__arrow" />
        {{ folder?.name }}
      </router-link>
    </div>

    <!-- MODEL -->
    <div v-if="model == null && isLoading">
      <p class="message-info">
        {{ $t("messages.loadingData") }}
      </p>
    </div>

    <model-detail
      v-if="model || !isLoading"
      :model="model"
      :editor-route="editorRoute"
      :model-workspace="modelWorkspace"
    >
      <template #header="{}" v-if="model != null">
        <template v-if="!data.editing">
          <div>
            <h1>
              {{ model.name }}
              <span
                v-if="model.isSimulationGame"
                class="modelentry__titlenotice"
              >
                ({{ $t("words.businessGame", 1) }})
              </span>
              <span v-if="model.isLocked" class="modelentry__titlenotice">
                ({{ $t("labels.locked") }})
              </span>
            </h1>
            <formatted-text :value="model.description" />
          </div>
          <button
            v-if="!model.isLocked && modelPermissions.canUpdate"
            type="button"
            class="button button_secondary"
            @click="startEditing"
          >
            {{ $t("labels.edit") }}
          </button>
        </template>
        <template v-else>
          <form
            id="view-workspacemodel-form"
            class="view-workspacemodel__form"
            @submit.prevent="endEditing"
          >
            <div class="column gap-4">
              <input-text
                v-model:value="data.name"
                :input-class="['view-workspacemodel__heading']"
                :label="$t('labels.name')"
              />
              <input-text-editor
                v-model:value="data.description"
                class="w-full"
                :label="$t('labels.description')"
              />
            </div>
          </form>

          <div class="view-workspacemodel__headerbuttons">
            <button
              class="button"
              type="submit"
              form="view-workspacemodel-form"
            >
              {{ $t("labels.save") }}
            </button>
            <button
              class="button button_secondary"
              type="button"
              @click="data.editing = false"
            >
              {{ $t("labels.abort") }}
            </button>
          </div>
        </template>
      </template>

      <template #buttons v-if="model != null">
        <template v-if="model.isLocked == false">
          <button
            class="button button_secondary model-detail__button"
            type="button"
            @click="data.isMoving = true"
          >
            {{ $t("labels.move") }}
          </button>
          <model-move-popup
            :visible="data.isMoving"
            :model="model"
            @close="data.isMoving = false"
            @moved="reloadRoute($event)"
          />
        </template>

        <template v-if="modelPermissions.canLock">
          <button
            v-if="!model.isLocked"
            class="button button_secondary model-detail__button"
            type="button"
            @click="lockModel()"
          >
            {{ $t("actions.lockModel") }}
          </button>
          <button
            v-if="model.isLocked"
            class="button button_secondary model-detail__button"
            type="button"
            @click="unlockModel()"
          >
            {{ $t("actions.unlockModel") }}
          </button>
        </template>
      </template>

      <template #main v-if="model != null">
        <!-- shared -->
        <div class="view-workspacemodel__sharelist">
          <workspace-model-share-list :can-share="license.limits.canShare !== false" :model="model" />
          <workspace-model-publication-list :workspace-id="modelWorkspace.id" :has-publishing-upgrade="!!modelPermissions.canPublish" :model-id="model.id" />
        </div>

        <hr />

        <workspace-model-version-history :model-id="model.id" />

        <template v-if="!model.isLocked && modelPermissions.canDelete">
          <hr />

          <button
            class="button button_secondary"
            type="button"
            @click="deleteModel"
          >
            {{ $t("actions.deleteModel") }}
          </button>
        </template>
      </template>
    </model-detail>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { showConfirm, showError } from "@core/ui/alert/alert";
import { useModels } from "@/api/models.store";
import { useWorkspaces } from "@/api/workspaces.store";
import { useLicense } from "@/api/license.store";

import { getRouteToFolder, getRouteToModel } from "@core/routes/routeDetector";

import LucideIcon from "@core/ui/LucideIcon.vue";
import FormattedText from "@core/ui/FormattedText.vue";

import InputText from "@core/input/InputText.vue";
import InputTextEditor from "@core/input/TextEditor.vue";

import WorkspaceModelShareList from "@manager/workspacemodels/WorkspaceModelShareList.vue";
import WorkspaceModelPublicationList from "@manager/workspacemodels/WorkspaceModelPublicationList.vue";
import WorkspaceModelVersionHistory from "@manager/workspacemodels/WorkspaceModelVersionHistory.vue";
import ModelDetail from "@manager/workspacemodels/ModelDetail.vue";
import ModelMovePopup from "../workspacemodels/ModelMovePopup.vue";

import type { WorkspaceFolder } from "@/api/Workspace";
import type { Model } from "@/api/Model";

const props = defineProps<{
  modelId: string | number;
  organizationSlug?: string;
  teamSlug?: string;
}>();

const router = useRouter();
const { t } = useI18n();
const license = useLicense();
const { currentWorkspace } = useWorkspaces(props);

const { currentModel, isLoading, updateModel, destroyModel } = useModels(props);

const model = currentModel;
const modelWorkspace = currentWorkspace;

const data = reactive({
  editing: false,
  name: "",
  description: "",
  publications: [],
  isDuplicating: false,
  isMoving: false
});

/**
 * The folder the model is in
 */
const folder = computed(() => {
  if (model.value == null) return null;
  const folders = modelWorkspace.value?.folders ?? [];
  return folders.find((f) => f.id == model.value?.folder);
});

const modelPermissions = computed(() => {
  return (
    modelWorkspace.value?.modelpermissions ?? model.value?.permissions ?? {}
  );
});

const editorRoute = computed(() => {
  if (model.value == null) return null;
  if (modelWorkspace.value == null) return { name: "models-explorer" };
  if (modelWorkspace.value.isPersonal) {
    return {
      name: "personal-workspace-model",
      params: {
        modelId: model.value.id
      }
    };
  } else {
    return {
      name: "team-model",
      params: {
        teamSlug: modelWorkspace.value.slug,
        organizationSlug: modelWorkspace.value.organizationSlug,
        modelId: model.value.id
      }
    };
  }
});

function startEditing() {
  data.editing = true;
  data.name = model.value?.name || "";
  data.description = model.value?.description || "";
}

async function endEditing() {
  data.editing = false;

  // save changes
  if (
    data.name != model.value?.name ||
    data.description != model.value?.description
  ) {
    try {
      if (!model.value) {
        throw new Error("Model not found");
      }
      return await updateModel({
        id: model.value.id,
        workspace: model.value.workspace,
        name: data.name,
        description: data.description
      });
    } catch (error) {
      showError(error as Error);
    }
  }
}

async function deleteModel() {
  if (
    await showConfirm(
      t("questions.confirmDeleteModel"),
      t("actions.deleteModel")
    )
  ) {
    try {
      if (!model.value) {
        throw new Error("Model not found");
      }
      const containingFolder = folder.value;
      await destroyModel(model.value);

      if (containingFolder) {
        console.log("model deleted, move to folder", containingFolder);
        navigateToWorkspaceFolder(containingFolder);
      }
    } catch (e) {
      const error = e as Error;
      console.error(error);
      if (error?.message) {
        showError(error);
      }
    }
  }
}

function navigateToWorkspaceFolder(folder: WorkspaceFolder) {
  const folderRoute = getRouteToFolder(folder);
  router.push(folderRoute);
}

function reloadRoute(updatedModel: Model) {
  const newRoute = getRouteToModel(updatedModel);
  console.log("reload", updatedModel, newRoute);
  router.replace(newRoute);
}

async function lockModel() {
  try {
    if (!model.value) {
      throw new Error("Model not found");
    }
    return await updateModel({
      id: model.value.id,
      workspace: model.value.workspace,
      isLocked: true
    });
  } catch (error) {
    showError(error as Error);
  }
}

async function unlockModel() {
  try {
    if (!model.value) {
      throw new Error("Model not found");
    }
    return await updateModel({
      id: model.value.id,
      workspace: model.value.workspace,
      isLocked: false
    });
  } catch (error) {
    showError(error as Error);
  }
}
</script>

<style lang="scss">
.view-workspacemodel {
  width: 100%;
  max-width: 60rem;
  margin: 0 auto;

  &__breadcrumps {
    margin-bottom: 1rem;
  }

  &__formheader {
    display: grid;
    width: 100%;
    gap: 1rem;
    grid-template-columns: 1fr min-content;
  }

  &__headerbuttons {
    gap: 0.5rem;
    display: flex;
    flex-direction: column;
  }

  &__heading {
    font-size: 2rem;
    font-weight: 700;
  }

  &__sharelist {
    padding: 1rem 0;
    border-top: 1px solid var(--color-border);
  }
}
</style>
